import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import classNames from 'classnames';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import translate from '@/lib/intl/translate';
import { formatDate } from '@/utils/dates';
import { showErrorMessage } from '@/utils/ToastFeedback';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import useConstituentIWaveConnectForm from '../hooks/useConstituentIWaveConnectForm';
import ConstituentConnectingLoader from './ConstituentConnectingLoader';

export default function ConstituentIWaveConnectForm({
  connected,
  constituentId,
  iWaveId,
  lastUpdated = '',
  onRefresh,
}: {
  connected: boolean;
  constituentId: string;
  iWaveId?: string;
  lastUpdated?: string;
  onRefresh(): Promise<void | APIResponse<object> | undefined>;
}) {
  const {
    connectionError,
    control,
    hasConnection,
    hasIWaveId,
    isSubmitting,
    isValid,
    onEditClick,
    onSubmit,
  } = useConstituentIWaveConnectForm(constituentId);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const handleEditClick = () => {
    onEditClick();
  };

  const handleRefreshClick = React.useCallback(async () => {
    if (isRefreshing) {
      return;
    }

    setIsRefreshing(true);

    try {
      await onRefresh();
    } catch (e) {
      showErrorMessage(translate('IWAVE_CONSTITUENT_UPDATE_ERROR'));
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing]);

  return (
    <div>
      {connectionError && (
        <Alert className="mb-2 inline-block" icon={false} severity="error">
          {connectionError}
        </Alert>
      )}

      {(connected || hasConnection) && iWaveId ? (
        <div>
          <div className="flex items-center">
            <span>
              {translate('IWAVE_CONSTITUENT_IWAVE_ID', { id: iWaveId })}
            </span>

            <IconButton
              aria-label={translate('IWAVE_INTEGRATION_CONNECTION_EDIT')}
              onClick={handleEditClick}
              size="small"
            >
              <EditIcon className="h-4 w-4" />
            </IconButton>
          </div>

          <div className="flex items-center">
            {isRefreshing ? (
              <ConstituentConnectingLoader>
                {translate('IWAVE_CONSTITUENT_UPDATING')}
              </ConstituentConnectingLoader>
            ) : (
              <>
                <Button
                  className="mr-2 p-0 type-body2"
                  onClick={handleRefreshClick}
                  variant="text"
                >
                  {translate('IWAVE_INTEGRATION_CONNECTION_UPDATE')}
                </Button>

                {lastUpdated && (
                  <span className="text-grey-600">
                    {translate('IWAVE_INTEGRATION_CONNECTION_REFRESH_DATE', {
                      instilDate: formatDate(lastUpdated),
                      iWaveDate: formatDate(lastUpdated),
                    })}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <form className="py-2" noValidate onSubmit={onSubmit}>
          <div className="mb-2 flex">
            <FormControl className="mr-2 w-80">
              <ControlledTextField
                className="flex-none"
                control={control}
                fullWidth
                id="i-wave-id"
                label={translate('IWAVE_CONSTITUENT_ID_LABEL')}
                name="iwaveUniqueScreeningId"
                required
                rules={{
                  required: translate('IWAVE_CONSTITUENT_ID_REQUIRED'),
                }}
              />
            </FormControl>

            <div
              className={classNames(
                'flex w-12 items-center justify-center rounded-lg p-2',
                {
                  'bg-primary-main text-primary-contrastText': hasIWaveId,
                  'bg-grey-100': !hasIWaveId,
                },
              )}
            >
              <CheckIcon />
            </div>
          </div>

          {isSubmitting ? (
            <ConstituentConnectingLoader>
              {translate('IWAVE_CONSTITUENT_CONNECTING')}
            </ConstituentConnectingLoader>
          ) : (
            <Button
              type="submit"
              className="block p-0 type-body2"
              disabled={!isValid}
              variant="text"
            >
              {translate('IWAVE_INTEGRATION_CONNECTION_CTA')}
            </Button>
          )}
        </form>
      )}
    </div>
  );
}
